import type { PhotoCandid } from '@/api/types/photo'
import { notifyPhotoPlacementChangedOnSpread } from '@/lib/photos/schedule-album-photos-refresh'
import type { PageSide } from '@/lib/photos/types/page-side'

/** The sides of one spread that hold a photo, per spread id. */
type SidesBySpread = Map<string, Set<PageSide>>

/** What this session knows of where a candid sits: spreadId -> the pages of it holding the photo. */
export type PhotoPlacementSides = ReadonlyMap<string, ReadonlyMap<string, ReadonlySet<PageSide>>>

const BOTH_SIDES: ReadonlySet<PageSide> = new Set<PageSide>(['left', 'right'])
const NO_SIDES: ReadonlySet<PageSide> = new Set<PageSide>()

/**
 * Where this session knows each candid sits, per spread and per page: candidId -> spreadId -> the
 * sides (`'left'` / `'right'` page) an image shape for that candid currently touches. An entry
 * exists once the session knows the spread's truth for that candid, and an EMPTY set records that
 * it is known to be gone from the spread (placed there once and since removed, or seen there when
 * the spread loaded and since moved away or deleted). A spread with no entry is simply unknown.
 *
 * Scoped per spread rather than a single flat "placed" boolean so that placing a photo on one
 * spread and then navigating to another doesn't lose the override — the backend job that
 * recomputes each candid's real `placedFlag` across the whole book only runs after logout, so
 * without this a photo placed earlier in the session would show as "not placed" the moment you
 * leave the spread you placed it on, in both the Photos tray and filename search (WMPCM
 * "Placed tag doesn't follow the session across spreads"). The pages matter to the Photos panel's
 * Placed field, which lists printed page numbers (`resolvePlacedPages`).
 */
const sidesByCandid = new Map<string, SidesBySpread>()

/**
 * Candids this session has directly observed being placed at least once (via
 * `setPhotoPlacementOverride(id, true, ...)` or `reconcilePlacementOverrides` discovering one on
 * canvas) — kept forever (session lifetime), even after `sidesByCandid` shows no spread holding
 * the candid. This is what lets the snapshot force a candid back
 * to placedFlag=false once we know for certain (from our own live canvas scan) that it isn't on
 * any spread we're tracking anymore, instead of merely going silent and falling back to the
 * server's cached `placedFlag` — which, for a candid we ourselves just placed and removed within
 * this same session, is racing to catch up with our EARLIER (place) action and can land seconds
 * later still showing true even though we already removed it (WMPCM "Placed tag reappears a few
 * seconds after removing a photo"). We only ever do this for candids we've directly tracked
 * ourselves; a candid this session has never touched is left alone entirely (absent from the
 * snapshot), so its display still always trusts the server — same as before. A candid merely
 * seen when a spread loaded (`observePlacementsOnSpread`) is not tracked in this sense: it may
 * sit on spreads this session has not visited.
 */
const sessionTrackedCandidIds = new Set<string>()

const listeners = new Set<() => void>()
const sideListeners = new Set<() => void>()
/** Immutable snapshot for useSyncExternalStore — must change reference when overrides change.
 *  true = force placed (on >=1 spread this session); false = this session placed it at some point
 *  and has since removed it from every spread it's tracked on — force NOT placed, since nothing
 *  else in the book could plausibly have caused the server to say otherwise except our own
 *  now-undone action. Absent = no opinion this session; `applyPhotoPlacementOverrides` leaves the
 *  server's own cached value untouched. */
let placementOverridesSnapshot: ReadonlyMap<string, boolean> = new Map()

/** Immutable snapshot for useSyncExternalStore — a new reference whenever any entry changes. */
let placementSidesSnapshot: PhotoPlacementSides = new Map()

function isPlacedSomewhere(candidId: string): boolean {
  for (const sides of sidesByCandid.get(candidId)?.values() ?? []) {
    if (sides.size > 0) return true
  }

  return false
}

function computePlacementOverrides(): Map<string, boolean> {
  const flat = new Map<string, boolean>()

  for (const candidId of sessionTrackedCandidIds) {
    flat.set(candidId, isPlacedSomewhere(candidId))
  }

  return flat
}

function areSameOverrides(a: ReadonlyMap<string, boolean>, b: ReadonlyMap<string, boolean>) {
  if (a.size !== b.size) return false

  for (const [candidId, placed] of a) {
    if (b.get(candidId) !== placed) return false
  }

  return true
}

function areSameSides(a: ReadonlySet<PageSide> | undefined, b: ReadonlySet<PageSide>): boolean {
  if (!a || a.size !== b.size) return false

  for (const side of a) {
    if (!b.has(side)) return false
  }

  return true
}

function syncPlacementSidesSnapshot(): void {
  const bySpreads = new Map<string, Map<string, Set<PageSide>>>()

  for (const [candidId, sidesBySpread] of sidesByCandid) {
    bySpreads.set(
      candidId,
      new Map([...sidesBySpread].map(([spreadId, sides]) => [spreadId, new Set(sides)]))
    )
  }

  placementSidesSnapshot = bySpreads
}

function emitSidePlacementChange() {
  syncPlacementSidesSnapshot()
  for (const listener of sideListeners) {
    listener()
  }
}

function emitPlacementChange() {
  placementOverridesSnapshot = computePlacementOverrides()
  for (const listener of listeners) {
    listener()
  }

  emitSidePlacementChange()
}

/** Records the sides of `spreadId` holding `candidId`; false when that is already what it says. */
function recordSides(candidId: string, spreadId: string, sides: ReadonlySet<PageSide>): boolean {
  let sidesBySpread = sidesByCandid.get(candidId)

  if (!sidesBySpread) {
    sidesBySpread = new Map()
    sidesByCandid.set(candidId, sidesBySpread)
  }

  if (areSameSides(sidesBySpread.get(spreadId), sides)) return false

  sidesBySpread.set(spreadId, new Set(sides))

  return true
}

export function subscribePhotoPlacementOverrides(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getPhotoPlacementOverridesSnapshot(): ReadonlyMap<string, boolean> {
  return placementOverridesSnapshot
}

export function subscribePhotoPlacementSides(listener: () => void): () => void {
  sideListeners.add(listener)
  return () => sideListeners.delete(listener)
}

export function getPhotoPlacementSidesSnapshot(): PhotoPlacementSides {
  return placementSidesSnapshot
}

/** Called when a candid's placement on `spreadId` changes — placed before server sync completes,
 * or removed (e.g. its last shape on that spread was deleted). Either direction schedules a
 * Photos tray refresh so its server-cached `placedFlag` catches up, even when there was no local
 * override to begin with (the common case: a photo placed in an earlier session, so the tray's
 * cached data already said "placed" with nothing local to clear). `sides` are the pages of the
 * spread the photo is on (both when the caller can't tell); a removal clears them all. */
export function setPhotoPlacementOverride(
  candidId: string,
  placed: boolean,
  spreadId: string,
  sides: ReadonlySet<PageSide> = BOTH_SIDES
): void {
  if (placed) {
    sessionTrackedCandidIds.add(candidId)

    if (!recordSides(candidId, spreadId, sides)) return

    emitPlacementChange()
    notifyPhotoPlacementChangedOnSpread()
    return
  }

  const wasPlacedOnSpread = (sidesByCandid.get(candidId)?.get(spreadId)?.size ?? 0) > 0
  const isNewInformation = recordSides(candidId, spreadId, NO_SIDES)

  if (wasPlacedOnSpread) {
    emitPlacementChange()
  } else if (isNewInformation) {
    // Only the side entries changed: the boolean overrides (and the trays that refresh when they
    // change) have no new opinion about a candid this session never placed.
    emitSidePlacementChange()
  }

  notifyPhotoPlacementChangedOnSpread()
}

/**
 * Makes `onCanvas` (candidId -> the sides of the spread it touches) the truth for `spreadId`:
 * every candid on it gets those sides, and every candid the session knew there that is no longer
 * on it is recorded as gone. Returns whether any entry changed.
 */
function applySpreadObservation(
  onCanvas: PlacementSidesOnCanvas,
  spreadId: string,
  track: boolean
): boolean {
  let changed = false

  for (const [candidId, sides] of onCanvas) {
    if (track) sessionTrackedCandidIds.add(candidId)

    if (recordSides(candidId, spreadId, sides)) changed = true
  }

  for (const [candidId, sidesBySpread] of sidesByCandid) {
    if (onCanvas.has(candidId)) continue

    if ((sidesBySpread.get(spreadId)?.size ?? 0) > 0) {
      sidesBySpread.set(spreadId, new Set())
      changed = true
    }
  }

  return changed
}

/** Candids on a spread's canvas, each with the pages of the spread it touches. */
export type PlacementSidesOnCanvas = ReadonlyMap<string, ReadonlySet<PageSide>>

/** Batch-sync `spreadId`'s overrides with canvas image shapes currently on it (undo/redo) —
 *  touches only that spread's own slice of each candid's entries, leaving every other
 *  spread's placements (this session) untouched. */
export function reconcilePlacementOverrides(
  onCanvas: PlacementSidesOnCanvas,
  spreadId: string
): void {
  if (applySpreadObservation(onCanvas, spreadId, true)) {
    emitPlacementChange()
  }
}

/**
 * Seeds `spreadId`'s entries from the photos on it as it first loads, the way ClikArt's tracking
 * is seeded, so a photo placed in an earlier session is known to this session and its pages drop
 * out of the Placed field once it is moved off or removed. Unlike `reconcilePlacementOverrides`
 * it does not track the candids: they may sit on spreads this session has not seen, so their
 * tray flags keep following the server. The boolean overrides, and the trays that refresh when
 * they change, are only notified if an already-tracked candid's flag changes.
 */
export function observePlacementsOnSpread(
  onCanvas: PlacementSidesOnCanvas,
  spreadId: string
): void {
  if (!applySpreadObservation(onCanvas, spreadId, false)) return

  if (areSameOverrides(computePlacementOverrides(), placementOverridesSnapshot)) {
    emitSidePlacementChange()
  } else {
    emitPlacementChange()
  }
}

/** Called at logout (session end) — a fresh login gets fresh server-computed `placedFlag` data
 *  from the backend's book-wide scan, so nothing session-local should carry over. Not called on
 *  spread switch: that's the whole point of tracking placements per spread (see module doc). */
export function clearPhotoPlacementOverrides(): void {
  if (sidesByCandid.size === 0 && sessionTrackedCandidIds.size === 0) return

  sidesByCandid.clear()
  sessionTrackedCandidIds.clear()
  emitPlacementChange()
}

export function applyPhotoPlacementOverrides(
  photos: PhotoCandid[],
  overrides: ReadonlyMap<string, boolean> = getPhotoPlacementOverridesSnapshot()
): PhotoCandid[] {
  if (overrides.size === 0) return photos

  return photos.map((photo) => {
    const placedFlag = overrides.get(photo.candidId)

    if (placedFlag === undefined || photo.placedFlag === placedFlag) return photo

    return { ...photo, placedFlag }
  })
}
